import React from "react";
import { useLanguage } from "../contexts/LanguageContext";
import Footer from "../components/Footer";
import LeftSVG from "../assets/page_svg/vector.svg";
import RightSVG from "../assets/page_svg/Daily-Routine.svg";
import WhatsAppButton from "../components/WhatsappButton";
import RondleidingForm from "../components/RondleidingForm";

const DailyRoutine: React.FC = () => {
  const { t } = useLanguage();
    const [openForm, setOpenForm] = React.useState<null | "rondleiding">(null);


  const morningSchedule = [
    {
      time: t("dailyRoutine.morning.dropOff.time"),
      title: t("dailyRoutine.morning.dropOff.title"),
      description: t("dailyRoutine.morning.dropOff.description"),
      icon: "🌅",
    },
    {
      time: t("dailyRoutine.morning.circle.time"),
      title: t("dailyRoutine.morning.circle.title"),
      description: t("dailyRoutine.morning.circle.description"),
      icon: "🍎",
    },
    {
      time: t("dailyRoutine.morning.activity.time"),
      title: t("dailyRoutine.morning.activity.title"),
      description: t("dailyRoutine.morning.activity.description"),
      icon: "🎨",
    },
    {
      time: t("dailyRoutine.morning.outdoor.time"),
      title: t("dailyRoutine.morning.outdoor.title"),
      description: t("dailyRoutine.morning.outdoor.description"),
      icon: "🌳",
    },
    {
      time: t("dailyRoutine.morning.lunch.time"),
      title: t("dailyRoutine.morning.lunch.title"),
      description: t("dailyRoutine.morning.lunch.description"),
      icon: "🍽️",
    },
  ];

  const afternoonSchedule = [
    {
      time: t("dailyRoutine.afternoon.rest.time"),
      title: t("dailyRoutine.afternoon.rest.title"),
      description: t("dailyRoutine.afternoon.rest.description"),
      icon: "😴",
    },
    {
      time: t("dailyRoutine.afternoon.snack.time"),
      title: t("dailyRoutine.afternoon.snack.title"),
      description: t("dailyRoutine.afternoon.snack.description"),
      icon: "🥕",
    },
    {
      time: t("dailyRoutine.afternoon.activity.time"),
      title: t("dailyRoutine.afternoon.activity.title"),
      description: t("dailyRoutine.afternoon.activity.description"),
      icon: "🎭",
    },
    {
      time: t("dailyRoutine.afternoon.meal.time"),
      title: t("dailyRoutine.afternoon.meal.title"),
      description: t("dailyRoutine.afternoon.meal.description"),
      icon: "🥖",
    },
    {
      time: t("dailyRoutine.afternoon.pickup.time"),
      title: t("dailyRoutine.afternoon.pickup.title"),
      description: t("dailyRoutine.afternoon.pickup.description"),
      icon: "📚",
    },
  ];

  const principles = [
    {
      title: t("dailyRoutine.principleItems.predictability.title"),
      description: t("dailyRoutine.principleItems.predictability.description"),
      icon: "🔄",
    },
    {
      title: t("dailyRoutine.principleItems.sharedMoments.title"),
      description: t("dailyRoutine.principleItems.sharedMoments.description"),
      icon: "🤝",
    },
    {
      title: t("dailyRoutine.principleItems.freePlay.title"),
      description: t("dailyRoutine.principleItems.freePlay.description"),
      icon: "🎪",
    },
    {
      title: t("dailyRoutine.principleItems.targeted.title"),
      description: t("dailyRoutine.principleItems.targeted.description"),
      icon: "🎯",
    },
    {
      title: t("dailyRoutine.principleItems.rest.title"),
      description: t("dailyRoutine.principleItems.rest.description"),
      icon: "💤",
    },
    {
      title: t("dailyRoutine.principleItems.attentionSpan.title"),
      description: t("dailyRoutine.principleItems.attentionSpan.description"),
      icon: "⏰",
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9DCAC" }}>
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 hero-section-h">
          {/* Decorative SVGs (scaled with container) */}
          <div className="absolute inset-0 pointer-events-none z-0">
            {/* Left SVG */}
            <img
              src={LeftSVG}
              alt="left decoration"
              className="absolute top-0 left-0 max-w-[150px] sm:max-w-[220px] lg:max-w-[280px] w-full opacity-100"
            />
            {/* Right SVG */}
            <img
              src={RightSVG}
              alt="right decoration"
              className="absolute bottom-[-100px] right-0 max-w-[220px] sm:max-w-[220px] md:max-w-[250px] lg:max-w-[320px] w-full opacity-100"
            />
          </div>
          <div className="max-w-6xl mx-auto text-center z-10 relative">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-child font-semibold text-black mb-6">
              {t("dailyRoutine.title")}
            </h1>
            <p className="text-xl md:text-2xl text-neutral-800 max-w-3xl mx-auto leading-relaxed">
              {t("dailyRoutine.subtitle")}
            </p>
          </div>
        </section>

        {/* Morning Schedule Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-child font-semibold text-black mb-6">
                {t("dailyRoutine.morningSchedule")}
              </h2>
              <p className="text-xl text-neutral-800 max-w-3xl mx-auto leading-relaxed">
                {t("dailyRoutine.morningDesc")}
              </p>
            </div>

            <div className="space-y-8">
              {morningSchedule.map((activity, index) => (
                <div
                  key={index}
                  className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft hover:shadow-warm transition-all duration-300"
                >
                  <div className="flex items-start gap-6">
                    <div className="text-4xl">{activity.icon}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-4">
                        <span className="bg-[#492A19] text-[#F9DCAC] px-4 py-2 rounded-full font-child font-semibold text-sm">
                          {activity.time}
                        </span>
                        <h3 className="text-2xl font-child font-semibold text-left text-black">
                          {activity.title}
                        </h3>
                      </div>
                      <p className="text-neutral-800 text-left leading-relaxed">
                        {activity.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Afternoon Schedule Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-child font-semibold text-black mb-6">
                {t("dailyRoutine.afternoonSchedule")}
              </h2>
              <p className="text-xl text-neutral-800 max-w-3xl mx-auto leading-relaxed">
                {t("dailyRoutine.afternoonDesc")}
              </p>
            </div>

            <div className="space-y-8">
              {afternoonSchedule.map((activity, index) => (
                <div
                  key={index}
                  className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft hover:shadow-warm transition-all duration-300"
                >
                  <div className="flex items-start gap-6">
                    <div className="text-4xl">{activity.icon}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-4">
                        <span className="bg-[#492A19] text-[#F9DCAC] px-4 py-2 rounded-full font-child font-semibold text-sm">
                          {activity.time}
                        </span>
                        <h3 className="text-2xl font-child font-semibold text-black">
                          {activity.title}
                        </h3>
                      </div>
                      <p className="text-neutral-800 text-left leading-relaxed">
                        {activity.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Principles Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-child font-semibold text-black mb-6">
                {t("dailyRoutine.principles")}
              </h2>
              <p className="text-xl text-neutral-800 max-w-3xl mx-auto leading-relaxed">
                {t("dailyRoutine.principlesDesc")}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {principles.map((principle, index) => (
                <div
                  key={index}
                  className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft hover:shadow-warm transition-all duration-300 transform hover:scale-105 text-center"
                >
                  <div className="text-5xl mb-6">{principle.icon}</div>
                  <h3 className="text-xl font-child font-semibold text-black mb-4">
                    {principle.title}
                  </h3>
                  <p className="text-neutral-800 leading-relaxed">
                    {principle.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center">
            {/* Heading */}
            <h2 className="text-3xl md:text-4xl font-child font-semibold text-black mb-6">
              {t("dailyRoutineCta.joinAdventures")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto leading-relaxed mb-12">
              {t("dailyRoutineCta.experienceText")}
            </p>

            {/* Features Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-16 mb-16">
              {/* Flexible Schedule */}
              <div className="bg-white rounded-3xl p-8 shadow-soft hover:shadow-warm transition-all duration-300 text-left">
                <h3 className="text-2xl font-child font-semibold text-black mb-4">
                  {t("dailyRoutineCta.flexibleScheduleTitle")}
                </h3>
                <p className="text-black mb-4">
                  {t("dailyRoutineCta.flexibleScheduleDesc")}
                </p>
                <ul className="list-disc list-inside text-neutral-800 space-y-2">
                  <li>{t("dailyRoutineCta.fullDayCare")}</li>
                  <li>{t("dailyRoutineCta.halfDayOptions")}</li>
                  <li>{t("dailyRoutineCta.dropInCare")}</li>
                </ul>
              </div>

              {/* Special Activities */}
              <div className="bg-white rounded-3xl p-8 shadow-soft hover:shadow-warm transition-all duration-300 text-left">
                <h3 className="text-2xl font-child font-semibold text-black mb-4">
                  {t("dailyRoutineCta.specialActivitiesTitle")}
                </h3>
                <p className="text-black mb-4">
                  {t("dailyRoutineCta.specialActivitiesDesc")}
                </p>
                <ul className="list-disc list-inside text-neutral-800 space-y-2">
                  <li>{t("dailyRoutineCta.artWorkshops")}</li>
                  <li>{t("dailyRoutineCta.musicClasses")}</li>
                  <li>{t("dailyRoutineCta.gardenTime")}</li>
                  <li>{t("dailyRoutineCta.fridayFun")}</li>
                </ul>
              </div>
            </div>

            {/* Call to Action Buttons */}
            <div className="flex justify-center gap-6">
              <button
                onClick={() => setOpenForm("rondleiding")}
                className="bg-[#492A19] text-[#F9DCAC] px-8 py-3 rounded-full font-child font-semibold text-sm lg:text-lg shadow-soft hover:shadow-warm transition-all duration-300"
              >
                {t("dailyRoutineCta.scheduleVisit")}
              </button>
              <a
                href="/contact"
                className="bg-transparent border-2 border-[#492A19] text-[#492A19] px-8 py-3 rounded-full font-child font-semibold text-sm lg:text-lg hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300"
              >
                {t("dailyRoutineCta.contactUs")}
              </a>
            </div>
          </div>
        </section>
      </div>

                    {/* Forms Modals */}
      {openForm === "rondleiding" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-6">
          <div className="bg-[#FBEACD] rounded-2xl p-8 max-w-lg w-full relative">
            <button
              onClick={() => setOpenForm(null)}
              className="absolute top-4 right-4 text-2xl"
            >
              &times;
            </button>
            <RondleidingForm />
          </div>
        </div>
      )}
      {/* WhatsApp Floating Contact Button */}
      <WhatsAppButton />
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default DailyRoutine;
